/** Row shape used for booking-form PDF guest tables (seat column is numeric string or em dash). */
export type BookingFormGuestRow = {
  guestName: string;
  tableName: string;
  seatNumber: string;
};

export function parseBookingFormSeatNumber(seatNumber: string): number | null {
  const trimmed = (seatNumber ?? '').trim();
  if (!trimmed || trimmed === '—' || trimmed === '-') {
    return null;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Seat number ascending (matches visual table planner global numbering).
 * Table name and guest name are tie-breakers; unassigned seats last.
 */
export function compareBookingFormGuestsBySeatAsc(
  a: BookingFormGuestRow,
  b: BookingFormGuestRow,
): number {
  const seatA = parseBookingFormSeatNumber(a.seatNumber);
  const seatB = parseBookingFormSeatNumber(b.seatNumber);
  if (seatA === null && seatB === null) {
    const nameCmp = (a.guestName || '').localeCompare(b.guestName || '', undefined, {
      sensitivity: 'base',
    });
    if (nameCmp !== 0) {
      return nameCmp;
    }
    return (a.tableName || '').localeCompare(b.tableName || '', undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  }
  if (seatA === null) {
    return 1;
  }
  if (seatB === null) {
    return -1;
  }
  if (seatA !== seatB) {
    return seatA - seatB;
  }

  const tableCmp = (a.tableName || '').localeCompare(b.tableName || '', undefined, {
    numeric: true,
    sensitivity: 'base',
  });
  if (tableCmp !== 0) {
    return tableCmp;
  }

  return (a.guestName || '').localeCompare(b.guestName || '', undefined, { sensitivity: 'base' });
}

export function sortBookingFormGuestsBySeatAsc<T extends BookingFormGuestRow>(guests: T[]): T[] {
  return [...guests].sort(compareBookingFormGuestsBySeatAsc);
}
